'use client';

import * as React from 'react';

// Self-healing error UI shared by the route error boundaries (app/error.tsx and
// app/(inbox)/error.tsx). Both wrap it so a transient client exception during a
// network blip / laptop sleep / tab backgrounding doesn't strand the operator
// on a dead screen.
//
// Why both levels: a Next.js error.tsx catches errors from the segments BELOW
// it, including nested layouts — but NOT its own segment's layout. The inbox
// boundary therefore can't catch a throw inside (inbox)/layout.tsx (e.g. its
// server render re-running on the router.refresh() that fires on socket
// reconnect). The root app/error.tsx does, so we need it too.
//
// Recovery: retry the failed render (reset()) as soon as the connection or the
// tab comes back — exactly when the transient cause has cleared. A module-scoped
// rolling cap stops a genuinely broken render from thrashing in a reset loop;
// past it we leave a manual reload.

const RETRY_WINDOW_MS = 10_000;
const MAX_RETRIES_PER_WINDOW = 3;

// Module scope so the cap survives the remount each reset() triggers — a
// component-local ref would reset to 0 every time the boundary re-throws.
let recentResetTimestamps: number[] = [];

function canAutoRetry(): boolean {
  const now = Date.now();
  recentResetTimestamps = recentResetTimestamps.filter(
    (t) => now - t < RETRY_WINDOW_MS,
  );
  if (recentResetTimestamps.length >= MAX_RETRIES_PER_WINDOW) return false;
  recentResetTimestamps.push(now);
  return true;
}

// When soft retries exhaust, the likely cause is DEPLOY VERSION SKEW: a
// long-lived tab talking to a newer server build (every RSC fetch / server
// action / reconnect-refresh then throws, and reset() re-renders the SAME
// stale client code, so it can never heal). The definitive cure is one full
// reload — it downloads the fresh build. Allowed once per window (tracked in
// sessionStorage so it survives the reload itself); if the error persists on
// the fresh build it's a genuine bug and we strand on the manual UI instead
// of reload-looping.
const HARD_RELOAD_WINDOW_MS = 5 * 60_000;
const HARD_RELOAD_KEY = 'staffy:error-hard-reload-at';

function tryHardReloadOnce(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(HARD_RELOAD_KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < HARD_RELOAD_WINDOW_MS) {
      return false;
    }
    window.sessionStorage.setItem(HARD_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — reload without loop protection is worse
    // than the manual UI; bail.
    return false;
  }
  window.location.reload();
  return true;
}

export function RecoverableError({
  error,
  reset,
  scope,
}: {
  error: Error & { digest?: string };
  reset: () => void;
  scope: string;
}): React.JSX.Element {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.error(`[${scope}] recovering from client error:`, error);

    const tryRecover = (): void => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return; // still offline — the 'online' listener will retry
      }
      if (!canAutoRetry()) {
        // Soft retries keep failing → probably version skew from a deploy;
        // one automatic full reload fetches the fresh build. Falls through
        // to the manual UI if we already reloaded recently.
        tryHardReloadOnce();
        return;
      }
      reset();
    };

    // Most of these errors are already stale by the time this paints (the blip
    // passed), so attempt a recovery shortly after mount.
    const t = setTimeout(tryRecover, 500);

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') tryRecover();
    };
    window.addEventListener('online', tryRecover);
    window.addEventListener('focus', tryRecover);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(t);
      window.removeEventListener('online', tryRecover);
      window.removeEventListener('focus', tryRecover);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [error, reset, scope]);

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--s-7)',
        background: 'var(--surface)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-sans)',
        zIndex: 9999,
      }}
    >
      <div style={{ maxWidth: 360, textAlign: 'center' }}>
        <div
          aria-hidden="true"
          style={{
            width: 44,
            height: 44,
            margin: '0 auto var(--s-5)',
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--r-pill)',
            background: 'color-mix(in oklch, var(--navy) 8%, transparent)',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: 'var(--text-secondary)' }}
          >
            <path d="M1 1l22 22" />
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
        </div>
        <h1
          style={{
            margin: '0 0 var(--s-3)',
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: 'var(--tracking-display)',
          }}
        >
          Connection interrupted
        </h1>
        <p
          style={{
            margin: '0 0 var(--s-6)',
            fontSize: 14,
            lineHeight: 1.5,
            color: 'var(--text-secondary)',
          }}
        >
          Reconnecting automatically. This usually clears on its own when your
          network comes back — if it doesn’t, reload the page.
        </p>
        <div
          style={{
            display: 'flex',
            gap: 'var(--s-4)',
            justifyContent: 'center',
          }}
        >
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              reset();
            }}
          >
            Try again
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}
